import { useEffect, useState, useCallback } from "react";
import { Button, Container, Group, Loader, Select, ActionIcon, Text, Divider, SimpleGrid, ScrollArea, Stack } from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { useNavigate } from "react-router-dom";
import { IconScale, IconPencil, IconPlus, IconTrash, IconTrashX, IconReceipt, IconFileImport, IconCalculator, IconUsers } from "@tabler/icons-react";
import HttpClient from "../helpers/HttpClient";
import { DataTable } from 'mantine-datatable';
import { Booking, BookingType } from "../types/BookingModel";
import axios from "axios";
import { config } from "../helpers/Config";
import { getAccessToken } from "../helpers/TokenRefresh";
import dayjs from "dayjs";

const VERTRAGSABRECHNUNG = "Gutschrift Vertragsabrechnung";
const PAGE_SIZE = 50;

interface BookingQuery {
  page: number;
  personId: string | null;
  bookingTypeId: string | null;
  from: Date | null;
  to: Date | null;
}

export interface PreloadBooking {
  bookingDate: Date;
  personId: string;
  personLabel: string;
  bookingTypeId: string;
  bookingTypeLabel: string;
  bookingTypeMultiplier: number;
  bookingText: string;
  bookingAmount: number;
}

function BookingPage() {
  const [loading, setLoading] = useState(true);
  const [records, setRecords] = useState<Booking[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [filteredSum, setFilteredSum] = useState(0);

  const currentYearFrom = new Date(new Date().getFullYear(), 0, 1);
  const currentYearTo = new Date(new Date().getFullYear(), 11, 31);

  const [query, setQuery] = useState<BookingQuery>({
    page: 1, personId: null, bookingTypeId: null, from: currentYearFrom, to: currentYearTo,
  });

  const [persons, setPersons] = useState<{ value: string; label: string }[]>([]);
  const [bookingTypes, setBookingTypes] = useState<{ value: string; label: string; multiplier: number }[]>([]);
  const [sepaLoading, setSepaLoading] = useState(false);
  const [selectedRecords, setSelectedRecords] = useState<Booking[]>([]);
  const [deleting, setDeleting] = useState(false);

  const [paymentTypeId, setPaymentTypeId] = useState<string | null>(null);
  const [pendingPayments, setPendingPayments] = useState<PreloadBooking[]>([]);

  const navigate = useNavigate();

  const downloadSepa = async () => {
    setSepaLoading(true);
    try {
      const token = await getAccessToken();
      const response = await axios.get(`${config.apiUrl}/sepa/direct-debit`, {
        responseType: "blob",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const url = URL.createObjectURL(new Blob([response.data], { type: "application/xml" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `sepa-dd-${new Date().toISOString().slice(0, 10)}.xml`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err: any) {
      const msg = err?.response?.data
        ? await err.response.data.text?.() ?? "Unknown error"
        : err?.message ?? "Unknown error";
      alert("SEPA export failed: " + msg);
    } finally {
      setSepaLoading(false);
    }
  };

  const fetchData = useCallback(async (q: BookingQuery) => {
    setLoading(true);
    try {
      const params: Record<string, string> = {
        skip: String((q.page - 1) * PAGE_SIZE),
        take: String(PAGE_SIZE),
      };
      if (q.personId) params.personId = q.personId;
      if (q.bookingTypeId) params.bookingTypeId = q.bookingTypeId;
      if (q.from) params.from = q.from.toISOString();
      if (q.to) params.to = q.to.toISOString();

      const result = await new HttpClient<Booking>('/booking').getAllPaged(params);
      setRecords(result.data ?? []);
      setTotalRecords(result.total);
      setFilteredSum(result.sum ?? 0);
    } catch (error) {
      console.error("API call failed:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const loadDropdowns = async () => {
      const [personList, typeList] = await Promise.all([
        new HttpClient<any>("/person").getAll({ slim: 'true' }),
        new HttpClient<BookingType>("/bookingType").getAll(),
      ]);
      setPersons(personList.map((p: any) => ({ value: p.id.toString(), label: `${p.lastName} ${p.firstName}` })));
      setBookingTypes(typeList.map((t: any) => ({ value: t.id.toString(), label: t.title, multiplier: t.multiplier ?? 0 })));
    };
    loadDropdowns().catch(console.error);
  }, []);

  useEffect(() => {
    setSelectedRecords([]);
    fetchData(query);
  }, [query]);

  const setFilter = (update: Partial<Omit<BookingQuery, 'page'>>) =>
    setQuery(prev => ({ ...prev, ...update, page: 1 }));

  const handleAddPayment = (record: Booking) => {
    if (!paymentTypeId) return;
    const alreadyAdded = pendingPayments.some(
      (p) => p.personId === record.person?.id?.toString() && p.bookingText.includes(record.bookingText)
    );
    if (alreadyAdded) return;
    const bt = bookingTypes.find((t) => t.value === paymentTypeId)!;
    setPendingPayments((prev) => [
      ...prev,
      {
        bookingDate: new Date(),
        personId: record.person?.id?.toString() ?? "",
        personLabel: `${record.person?.lastName ?? ""} ${record.person?.firstName ?? ""}`,
        bookingTypeId: paymentTypeId,
        bookingTypeLabel: bt.label,
        bookingTypeMultiplier: bt.multiplier,
        bookingText: `Ausgleich: ${record.bookingText}`,
        bookingAmount: record.bookingAmount,
      },
    ]);
  };

  const handleGoToBatch = () => {
    navigate("/booking/create", { state: { preload: pendingPayments } });
  };

  const handleBatchDelete = async () => {
    if (selectedRecords.length === 0) return;
    if (!confirm(`${selectedRecords.length} Buchung(en) löschen?`)) return;
    setDeleting(true);
    try {
      const client = new HttpClient<Booking>('/booking');
      await Promise.all(selectedRecords.map((r) => client.delete(r.id)));
      setSelectedRecords([]);
      fetchData(query);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <Container fluid>
        {/* Header */}
        <Group justify="space-between" align="center" mb="xs" wrap="nowrap">
          <h1 style={{ margin: 0 }}>Buchungen</h1>
          <Group gap="xs" wrap="nowrap">
            <ActionIcon variant="filled" size="xl" radius="xl" onClick={() => navigate("/booking/create")}>
              <IconPlus style={{ width: "70%", height: "70%" }} stroke={1.5} />
            </ActionIcon>
            <ActionIcon variant="light" size="xl" radius="xl" onClick={() => navigate("/booking/wizard")} title="Buchung mit Beleg">
              <IconReceipt style={{ width: "60%", height: "60%" }} stroke={1.5} />
            </ActionIcon>
            <ActionIcon variant="light" size="xl" radius="xl" onClick={() => navigate("/booking/batch-wizard")} title="Stapelbuchung">
              <IconUsers style={{ width: "60%", height: "60%" }} stroke={1.5} />
            </ActionIcon>
            <Button variant="light" loading={sepaLoading} onClick={downloadSepa} visibleFrom="sm">
              SEPA DD herunterladen
            </Button>
          </Group>
        </Group>
        <Button variant="light" loading={sepaLoading} onClick={downloadSepa} hiddenFrom="sm" fullWidth mb="xs">
          SEPA DD herunterladen
        </Button>

        <Group mb="xs" gap="xs">
          <Button variant="light" leftSection={<IconFileImport size={16} />} onClick={() => navigate('/booking/import')}>
            Zahlungen importieren
          </Button>
          <Button variant="light" leftSection={<IconCalculator size={16} />} onClick={() => navigate('/calculation')}>
            Verträge abrechnen
          </Button>
        </Group>

        {/* Filters */}
        <SimpleGrid cols={{ base: 1, xs: 2, md: 4 }} mb="xs">
          <DatePickerInput
            clearable valueFormat="DD.MM.YYYY" label="Von"
            value={query.from} onChange={(v: string | null) => setFilter({ from: v ? new Date(v) : null })}
          />
          <DatePickerInput
            clearable valueFormat="DD.MM.YYYY" label="Bis"
            value={query.to} onChange={(v: string | null) => setFilter({ to: v ? new Date(v) : null })}
          />
          <Select
            label="Person" placeholder="Alle" clearable searchable
            data={persons} value={query.personId} onChange={(v) => setFilter({ personId: v })}
          />
          <Select
            label="Buchungsart" placeholder="Alle" clearable
            data={bookingTypes} value={query.bookingTypeId} onChange={(v) => setFilter({ bookingTypeId: v })}
          />
        </SimpleGrid>
        <Group mb="md">
          <Button variant="subtle"
            onClick={() => setQuery({ page: 1, personId: null, bookingTypeId: null, from: currentYearFrom, to: currentYearTo })}>
            Zurücksetzen
          </Button>
          {selectedRecords.length > 0 && (
            <Button
              color="red"
              variant="light"
              loading={deleting}
              leftSection={<IconTrashX size={16} />}
              onClick={handleBatchDelete}
            >
              {selectedRecords.length} löschen
            </Button>
          )}
        </Group>

        {/* Ausgleich */}
        <Stack gap="xs" mb="md">
          <Select
            label="Zahlungsart für Ausgleich"
            placeholder="Buchungsart wählen"
            data={bookingTypes}
            value={paymentTypeId}
            onChange={setPaymentTypeId}
            w={{ base: '100%', sm: 260 }}
          />
          {pendingPayments.length > 0 && (
            <Button leftSection={<IconScale size={16} />} onClick={handleGoToBatch} w={{ base: '100%', sm: 'auto' }}>
              Ausgleichszahlungen buchen ({pendingPayments.length})
            </Button>
          )}
        </Stack>
      </Container>

      <Container fluid>
        {loading && records.length === 0 ? (
          <Loader size="md" />
        ) : (
          <>
            <ScrollArea>
              <DataTable
                withTableBorder
                borderRadius="sm"
                withColumnBorders
                striped
                highlightOnHover
                fetching={loading}
                records={records}
                selectedRecords={selectedRecords}
                onSelectedRecordsChange={setSelectedRecords}
                columns={[
                  {
                    accessor: 'bookingDate',
                    title: 'Datum',
                    width: 110,
                    render: ({ bookingDate }) => dayjs(bookingDate).format("DD.MM.YYYY"),
                  },
                  {
                    accessor: 'person',
                    title: 'Person',
                    render: ({ person }) => person ? `${person.lastName} ${person.firstName}` : '',
                  },
                  {
                    accessor: 'bookingType',
                    title: 'Buchungsart',
                    visibleMediaQuery: (theme) => `(min-width: ${theme.breakpoints.sm})`,
                    render: ({ bookingType }) => bookingType?.title ?? '',
                  },
                  {
                    accessor: 'bookingText',
                    title: 'Text',
                    visibleMediaQuery: (theme) => `(min-width: ${theme.breakpoints.sm})`,
                  },
                  {
                    accessor: 'bookingAmount',
                    title: 'Betrag',
                    textAlign: 'right',
                    render: ({ bookingAmount, bookingType }) =>
                      (Number(bookingAmount) * (bookingType?.multiplier ?? 1)).toLocaleString("de-DE", {
                        style: "currency", currency: "EUR",
                      }),
                  },
                  {
                    accessor: 'actions',
                    title: '',
                    width: 80,
                    render: (record) => {
                      const isVertragsabrechnung = record.bookingType?.title === VERTRAGSABRECHNUNG;
                      const alreadyPending = pendingPayments.some(
                        (p) => p.personId === record.person?.id?.toString() && p.bookingText.includes(record.bookingText)
                      );
                      return (
                        <Group gap={4} justify="center">
                          <ActionIcon variant="light" color="blue" onClick={() => navigate(`/booking/edit/${record.id}`)}>
                            <IconPencil size={16} />
                          </ActionIcon>
                          {isVertragsabrechnung && (
                            <ActionIcon
                              variant="light"
                              color={alreadyPending ? "gray" : "teal"}
                              disabled={!paymentTypeId || alreadyPending}
                              title={!paymentTypeId ? "Zahlungsart wählen" : alreadyPending ? "Bereits hinzugefügt" : "Ausgleich hinzufügen"}
                              onClick={() => handleAddPayment(record)}
                            >
                              <IconScale size={16} />
                            </ActionIcon>
                          )}
                        </Group>
                      );
                    },
                  },
                ]}
                totalRecords={totalRecords}
                recordsPerPage={PAGE_SIZE}
                page={query.page}
                onPageChange={(p) => setQuery(prev => ({ ...prev, page: p }))}
              />
            </ScrollArea>
            <Group justify="flex-end" mt="xs" mb="md">
              <Text fw={600}>
                Summe ({totalRecords} Einträge):&nbsp;
                {filteredSum.toLocaleString("de-DE", { style: "currency", currency: "EUR" })}
              </Text>
            </Group>
          </>
        )}

        {pendingPayments.length > 0 && (
          <>
            <Divider my="lg" label={<Text fw={600}>Ausstehende Ausgleichszahlungen ({pendingPayments.length})</Text>} />
            <ScrollArea>
              <DataTable
                withTableBorder
                withColumnBorders
                mb="md"
                records={pendingPayments}
                columns={[
                  { accessor: 'personLabel', title: 'Person' },
                  { accessor: 'bookingText', title: 'Text' },
                  {
                    accessor: 'bookingAmount',
                    title: 'Betrag',
                    textAlign: 'right',
                    render: ({ bookingAmount }) =>
                      Number(bookingAmount).toLocaleString("de-DE", { style: "currency", currency: "EUR" }),
                  },
                  {
                    accessor: 'remove',
                    title: '',
                    width: 48,
                    render: (_, index) => (
                      <ActionIcon variant="light" color="red"
                        onClick={() => setPendingPayments((prev) => prev.filter((_, j) => j !== index))}>
                        <IconTrash size={16} />
                      </ActionIcon>
                    ),
                  },
                ]}
              />
            </ScrollArea>
            <Group wrap="wrap">
              <Button leftSection={<IconScale size={16} />} onClick={handleGoToBatch} fullWidth={{ base: true, xs: false } as any}>
                Zur Stapelbuchung ({pendingPayments.length})
              </Button>
              <Button variant="subtle" color="red" onClick={() => setPendingPayments([])}>Alle entfernen</Button>
            </Group>
          </>
        )}
      </Container>
    </>
  );
}

export default BookingPage;
